import { onUnmounted, ref } from 'vue';
import { useAutopilotStore } from '@llmor/autopilot-vue';
import {
  formatAutopilotError,
  shouldSurfaceClientError,
  type AutopilotUiError,
} from '@/autopilot/formatAutopilotError';

/**
 * Surfaces Autopilot failures the chat UI currently swallows: HTTP errors on
 * conversation POSTs ("interactions"), and user-facing client `error` events.
 * Connection failures already have their own banner via `store.connectionError`.
 */
export function useAutopilotRuntimeError() {
  const store = useAutopilotStore();
  const error = ref<AutopilotUiError | null>(null);
  const client = store.client;
  const originalInteract = client.interactWithConversation.bind(client);

  function clear(): void {
    error.value = null;
  }

  function capture(thrown: unknown): AutopilotUiError {
    const formatted = formatAutopilotError(thrown);
    error.value = formatted;
    return formatted;
  }

  client.interactWithConversation = async (data: Record<string, any>) => {
    try {
      const result = await originalInteract(data);
      error.value = null;
      return result;
    } catch (thrown) {
      capture(thrown);
      throw thrown;
    }
  };

  const offError = client.on('error', ({ context, error: thrown }) => {
    if (!shouldSurfaceClientError(context)) return;
    capture(thrown);
  });

  onUnmounted(() => {
    client.interactWithConversation = originalInteract;
    offError();
  });

  return { error, clear, capture };
}
